import { useState, useEffect, useRef, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import Modal, { ModalHeader, ModalBody, ModalFooter } from '../components/Modal';

const METHODS = ['cash', 'stripe', 'bank', 'online'];
const METHOD_LABELS = { cash: 'Cash', stripe: 'Stripe · Card', bank: 'Bank Transfer', online: 'Online (pre-paid)' };

function blank() {
  return {
    customer: '', email: '', addr: '',
    vehicle: '', vid: '',
    contract: '',
    method: 'cash',
    issueDate: new Date().toISOString().split('T')[0],
    dueDate: '',
    vatRate: 19,
    discount: 0,
    discType: 'eur',
    notes: '',
    status: 'draft',
  };
}

function blankLine() { return { _id: Math.random(), desc: '', qty: 1, unit: 0, total: 0 }; }

/* ── Reusable searchable dropdown ─────────────────────────────────────── */
function SearchDropdown({ label, value, onChange, options, placeholder, renderOption, renderValue, allowFreeText, freeTextLabel }) {
  const [open, setOpen]     = useState(false);
  const [query, setQuery]   = useState('');
  const ref                 = useRef(null);

  /* close on outside click — if allowFreeText and query typed, commit it */
  useEffect(() => {
    function handler(e) {
      if (ref.current && !ref.current.contains(e.target)) {
        if (allowFreeText && query.trim()) {
          onChange({ _freeText: true, label: query.trim() });
        }
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [query, allowFreeText, onChange]);

  const filtered = query.length === 0
    ? options
    : options.filter(o => renderOption(o).toLowerCase().includes(query.toLowerCase()));

  function select(opt) {
    onChange(opt);
    setQuery('');
    setOpen(false);
  }

  function commitFreeText() {
    if (!query.trim()) return;
    onChange({ _freeText: true, label: query.trim() });
    setQuery('');
    setOpen(false);
  }

  function clear(e) {
    e.stopPropagation();
    onChange(null);
    setQuery('');
  }

  const displayValue = value
    ? (value._freeText ? value.label : renderValue(value))
    : null;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <label className="fl">{label}</label>
      <div
        className="fi"
        style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: 6, paddingRight: 6 }}
        onClick={() => setOpen(o => !o)}
      >
        {open ? (
          <input
            autoFocus
            className="fi"
            style={{ border: 'none', background: 'transparent', padding: 0, flex: 1, fontSize: 12 }}
            value={query}
            placeholder={`Search ${placeholder}...`}
            onChange={e => setQuery(e.target.value)}
            onClick={e => e.stopPropagation()}
            onKeyDown={e => { if (e.key === 'Enter' && allowFreeText) commitFreeText(); }}
          />
        ) : (
          <span style={{ flex: 1, fontSize: 12, color: displayValue ? 'var(--tx)' : 'var(--tx3)' }}>
            {displayValue || placeholder}
          </span>
        )}
        {displayValue && !open && (
          <span onClick={clear} style={{ fontSize: 14, color: 'var(--tx3)', lineHeight: 1, cursor: 'pointer' }}>×</span>
        )}
        <span style={{ fontSize: 10, color: 'var(--tx3)' }}>{open ? '▲' : '▼'}</span>
      </div>

      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 999,
          background: 'var(--sur)', border: '1px solid var(--bd)', borderRadius: 6,
          boxShadow: '0 4px 16px rgba(0,0,0,.12)', maxHeight: 220, overflowY: 'auto', marginTop: 2,
        }}>
          {/* Free-text "use as new" option */}
          {allowFreeText && query.trim() && (
            <div
              onMouseDown={commitFreeText}
              style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 11, borderBottom: '1px solid var(--bd)', background: 'var(--bl-l)', color: 'var(--bl)', fontWeight: 600 }}
            >
              ✏️ {freeTextLabel || 'Use'} "{query.trim()}"
            </div>
          )}
          {filtered.length === 0 && !query.trim() && (
            <div style={{ padding: '10px 12px', fontSize: 11, color: 'var(--tx3)' }}>No results — type a name above</div>
          )}
          {filtered.map((opt, i) => (
            <div
              key={i}
              onMouseDown={() => select(opt)}
              style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 11, borderBottom: '1px solid var(--bd)', background: 'transparent' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--sur2)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              {opt._jsx || renderOption(opt)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────── */

export default function InvoiceCreateModal() {
  const { modals, closeModal, addInvoice, nextInvoiceNum, vehicles, rentals, rentalHistory, customers, addCustomer, showToast, companySettings } = useApp();
  const open = !!modals['create-invoice'];
  const defaultVatMode = companySettings?.vatMode || 'inclusive';
  const defaultVatRate = parseFloat(companySettings?.vatRate || 19);

  const [f, setF]         = useState(blank);
  const [lines, setLines] = useState([blankLine()]);
  const [vatMode, setVatMode] = useState(defaultVatMode);

  /* selected objects for the dropdowns */
  const [selCustomer,    setSelCustomer]    = useState(null);
  const [selVehicle,     setSelVehicle]     = useState(null);
  /* new customer draft — shown as inline form before saving */
  const [newCustDraft,   setNewCustDraft]   = useState(null);

  useEffect(() => {
    if (open) {
      setF({ ...blank(), vatRate: defaultVatRate });
      setVatMode(defaultVatMode);
      setLines([blankLine()]);
      setSelCustomer(null);
      setSelVehicle(null);
      setNewCustDraft(null);
    }
  }, [open]);

  function close() { closeModal('create-invoice'); }
  function upd(k, v) { setF(p => ({ ...p, [k]: v })); }

  const pickCustomer = useCallback((c) => {
    if (!c) {
      setSelCustomer(null);
      setNewCustDraft(null);
      setF(p => ({ ...p, customer: '', email: '', addr: '' }));
      return;
    }

    if (c._freeText) {
      /* Show inline form — don't save yet */
      const parts = c.label.trim().split(' ');
      setNewCustDraft({
        fname: parts[0] || c.label,
        lname: parts.slice(1).join(' ') || '',
        email: '', phone: '', address: '', notes: '',
      });
      setSelCustomer(null);
      return;
    }

    /* Existing customer selected */
    setNewCustDraft(null);
    setSelCustomer(c);
    setF(p => ({
      ...p,
      customer: `${c.fname} ${c.lname}`.trim(),
      email:    c.email   || p.email,
      addr:     c.address || p.addr,
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function saveNewCustomer() {
    if (!newCustDraft.fname.trim()) { alert('First name is required'); return; }
    const newCust = {
      id: 'C' + Date.now(),
      ...newCustDraft,
      createdAt: new Date().toISOString().split('T')[0],
    };
    addCustomer(newCust);
    setSelCustomer(newCust);
    setNewCustDraft(null);
    setF(p => ({
      ...p,
      customer: `${newCust.fname} ${newCust.lname}`.trim(),
      email:    newCust.email   || p.email,
      addr:     newCust.address || p.addr,
    }));
    showToast(`✅ Customer "${newCust.fname} ${newCust.lname}" saved`);
  }

  const pickVehicle = useCallback((v) => {
    if (!v) { setSelVehicle(null); setF(p => ({ ...p, vehicle: '', vid: '' })); return; }
    setSelVehicle(v);
    setF(p => ({ ...p, vehicle: v.name, vid: v.id }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function updLine(i, k, v) {
    setLines(prev => prev.map((l, idx) => {
      if (idx !== i) return l;
      const updated = { ...l, [k]: v };
      if (k === 'qty' || k === 'unit') updated.total = Number(updated.qty) * Number(updated.unit);
      return updated;
    }));
  }

  function addLine()       { setLines(prev => [...prev, blankLine()]); }
  function removeLine(i)   { setLines(prev => prev.filter((_, idx) => idx !== i)); }

  const subtotal  = lines.reduce((s, l) => s + Number(l.total), 0);
  const discAmt   = f.discType === '%' ? subtotal * (Number(f.discount) / 100) : Number(f.discount);
  const afterDisc = subtotal - discAmt;
  // VAT: inclusive = extract from price, exclusive = add on top
  const vatAmt    = vatMode === 'inclusive'
    ? afterDisc - afterDisc / (1 + Number(f.vatRate) / 100)
    : afterDisc * Number(f.vatRate) / 100;
  const total     = vatMode === 'inclusive' ? afterDisc : afterDisc + vatAmt;

  function fmt(n) { return Number(n).toFixed(2); }
  function fmtDate(iso) {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  }

  function submit() {
    if (!f.customer.trim()) { alert('Customer name is required'); return; }
    if (lines.length === 0 || lines.every(l => !l.desc)) { alert('Add at least one line item'); return; }

    const isDraft = f.status === 'draft';
    const num = isDraft ? null : nextInvoiceNum();
    const id = isDraft ? ('DRF-' + Date.now()) : num;

    addInvoice({
      id, num,
      customer: f.customer, email: f.email, addr: f.addr,
      vehicle: f.vehicle, vid: f.vid,
      contract: f.contract,
      method: f.method, methodLabel: METHOD_LABELS[f.method] || f.method,
      issue: fmtDate(f.issueDate), due: fmtDate(f.dueDate),
      issueDate: f.issueDate, dueDate: f.dueDate,
      lines: lines.map(l => ({ ...l, qty: Number(l.qty), unit: Number(l.unit), total: Number(l.total) })),
      subtotal, vatRate: Number(f.vatRate), vatAmt, vatMode,
      discount: Number(f.discount), discType: f.discType,
      total, notes: f.notes, status: f.status, source: 'manual',
      createdAt: new Date().toISOString(),
    });
    showToast(isDraft ? '📝 Draft saved' : `✅ Pro forma ${num} created`);
    close();
  }

  /* dropdown option builders */
  const customerOptions = customers.map(c => ({
    ...c,
    _jsx: (
      <div>
        <div style={{ fontWeight: 600 }}>{c.fname} {c.lname}</div>
        <div style={{ fontSize: 10, color: 'var(--tx3)' }}>{c.email}{c.phone ? ` · ${c.phone}` : ''}</div>
      </div>
    ),
  }));

  const vehicleOptions = vehicles.map(v => ({
    ...v,
    _jsx: (
      <div>
        <div style={{ fontWeight: 600 }}>{v.name}</div>
        <div style={{ fontSize: 10, color: 'var(--tx3)' }}>{v.plate} · {v.type}</div>
      </div>
    ),
  }));

  return (
    <Modal open={open} onClose={close} size="lg">
      <ModalHeader
        icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 16, height: 16 }}><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14,2 14,8 20,8"/></svg>}
        title="New Pro Forma"
        subtitle="Create a manual pro forma document"
        onClose={close}
      />

      <ModalBody>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>

          {/* ── Left col ── */}
          <div>
            {/* Customer searchable dropdown */}
            <div className="fg">
              <SearchDropdown
                label="Customer *"
                value={selCustomer}
                onChange={pickCustomer}
                options={customerOptions}
                placeholder="Search existing or type new name"
                renderOption={c => `${c.fname} ${c.lname} ${c.email || ''}`}
                renderValue={c => `${c.fname} ${c.lname}`}
                allowFreeText
                freeTextLabel="Add new customer"
              />
            </div>

            {/* ── New customer inline form ── */}
            {newCustDraft && (
              <div style={{ background: 'var(--bl-l)', border: '1.5px solid var(--bl-b)', borderRadius: 8, padding: 12, marginBottom: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--bl)', marginBottom: 8 }}>👤 New Customer Details</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                  <div className="fg" style={{ margin: 0 }}>
                    <label className="fl">First Name *</label>
                    <input className="fi" value={newCustDraft.fname}
                      onChange={e => setNewCustDraft(p => ({ ...p, fname: e.target.value }))}
                      placeholder="First name" />
                  </div>
                  <div className="fg" style={{ margin: 0 }}>
                    <label className="fl">Last Name</label>
                    <input className="fi" value={newCustDraft.lname}
                      onChange={e => setNewCustDraft(p => ({ ...p, lname: e.target.value }))}
                      placeholder="Last name" />
                  </div>
                  <div className="fg" style={{ margin: 0 }}>
                    <label className="fl">Email</label>
                    <input className="fi" value={newCustDraft.email}
                      onChange={e => setNewCustDraft(p => ({ ...p, email: e.target.value }))}
                      placeholder="email@example.com" />
                  </div>
                  <div className="fg" style={{ margin: 0 }}>
                    <label className="fl">Phone</label>
                    <input className="fi" value={newCustDraft.phone}
                      onChange={e => setNewCustDraft(p => ({ ...p, phone: e.target.value }))}
                      placeholder="+357 99 000000" />
                  </div>
                  <div className="fg" style={{ margin: 0, gridColumn: '1/-1' }}>
                    <label className="fl">Address</label>
                    <input className="fi" value={newCustDraft.address}
                      onChange={e => setNewCustDraft(p => ({ ...p, address: e.target.value }))}
                      placeholder="City, Country" />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                  <button className="btn btn-primary btn-sm" onClick={saveNewCustomer}>💾 Save Customer</button>
                  <button className="btn btn-ghost btn-sm" onClick={() => setNewCustDraft(null)}>Cancel</button>
                </div>
              </div>
            )}

            {/* Only show invoice email/addr fields if no new customer form open */}
            {!newCustDraft && (
              <>
                <div className="fg">
                  <label className="fl">Email</label>
                  <input className="fi" value={f.email} onChange={e => upd('email', e.target.value)} placeholder="customer@email.com" />
                </div>
                <div className="fg">
                  <label className="fl">Address</label>
                  <input className="fi" value={f.addr} onChange={e => upd('addr', e.target.value)} placeholder="City, Country" />
                </div>
              </>
            )}
          </div>

          {/* ── Right col ── */}
          <div>
            {/* Vehicle searchable dropdown */}
            <div className="fg">
              <SearchDropdown
                label="Vehicle (optional)"
                value={selVehicle}
                onChange={pickVehicle}
                options={vehicleOptions}
                placeholder="Search by name or plate"
                renderOption={v => `${v.name} ${v.plate}`}
                renderValue={v => `${v.name} · ${v.plate}`}
              />
            </div>

            <div className="fg">
              <label className="fl">
                Contract / Ref
                <span style={{ fontWeight: 400, color: 'var(--tx3)', marginLeft: 6, fontSize: 10 }}>
                  link this pro forma to a specific contract (optional)
                </span>
              </label>
              {(() => {
                // Build a list of all known contracts (active rentals + history). Filter by selected
                // vehicle / customer when possible so the operator can't tag wrong ones by accident.
                const all = [...(rentals || []), ...(rentalHistory || [])];
                // Strict preference: matches selected vehicle AND matches selected customer
                const matchVid = selVehicle ? all.filter(r => r.vid === selVehicle.id) : [];
                const matchCustomer = selCustomer ? all.filter(r => r.customer === `${selCustomer.fname} ${selCustomer.lname}`.trim() || r.customer === selCustomer.company) : [];
                // Best match = intersection of both
                const best = matchVid.filter(r => matchCustomer.includes(r));
                // De-duplicate ids
                const dedupe = list => {
                  const seen = new Set();
                  return list.filter(r => { if (seen.has(r.id)) return false; seen.add(r.id); return true; });
                };
                const bestUnique  = dedupe(best);
                const widerUnique = dedupe([...matchVid, ...matchCustomer, ...all]).filter(r => !bestUnique.find(b => b.id === r.id));
                const label = r => {
                  const v = vehicles.find(vv => vv.id === r.vid);
                  const isLease = r.type === 'lease';
                  const amt = isLease ? `€${r.monthlyRate || 0}/mo` : `€${r.rate || 0}/day`;
                  return `${r.id} · ${r.customer || '?'} · ${v?.name || r.vid || ''} · ${amt} · ${isLease ? 'lease' : 'rental'}`;
                };
                return (
                  <select className="fi" value={f.contract || ''} onChange={e => upd('contract', e.target.value)}>
                    <option value="">— Not linked to a contract (standalone) —</option>
                    {bestUnique.length > 0 && (
                      <optgroup label="✅ Best match — same vehicle & customer">
                        {bestUnique.map(r => <option key={r.id} value={r.id}>{label(r)}</option>)}
                      </optgroup>
                    )}
                    {widerUnique.length > 0 && (
                      <optgroup label={bestUnique.length > 0 ? 'Other contracts' : 'All contracts'}>
                        {widerUnique.map(r => <option key={r.id} value={r.id}>{label(r)}</option>)}
                      </optgroup>
                    )}
                  </select>
                );
              })()}
              {f.contract && !([...(rentals || []), ...(rentalHistory || [])].find(r => r.id === f.contract)) && (
                <div style={{ fontSize: 10, color: 'var(--am)', marginTop: 4 }}>
                  ⚠ Contract <strong>{f.contract}</strong> does not match any active or historical record.
                </div>
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div className="fg">
                <label className="fl">Issue Date</label>
                <input className="fi" type="date" value={f.issueDate} onChange={e => upd('issueDate', e.target.value)} />
              </div>
              <div className="fg">
                <label className="fl">Due Date</label>
                <input className="fi" type="date" value={f.dueDate} onChange={e => upd('dueDate', e.target.value)} />
              </div>
            </div>
          </div>
        </div>

        {/* ── Line Items ── */}
        <div style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx2)' }}>Line Items</div>
            <button className="btn btn-ghost btn-sm" onClick={addLine}>+ Add Line</button>
          </div>

          <div style={{ border: '1px solid var(--bd)', borderRadius: 'var(--r)', overflow: 'hidden' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '3fr 60px 80px 80px 28px', background: 'var(--sur2)', padding: '5px 8px', fontSize: 10, fontWeight: 600, color: 'var(--tx3)' }}>
              <div>Description</div><div>Qty</div><div>Unit €</div><div>Total €</div><div></div>
            </div>
            {lines.map((l, i) => (
              <div key={l._id} style={{ display: 'grid', gridTemplateColumns: '3fr 60px 80px 80px 28px', borderTop: '1px solid var(--bd)', padding: '3px 8px', alignItems: 'center' }}>
                <input className="fi" style={{ border: 'none', background: 'transparent', padding: '3px 0' }}
                  value={l.desc} onChange={e => updLine(i, 'desc', e.target.value)} placeholder="Item description" />
                <input className="fi" style={{ border: 'none', background: 'transparent', padding: '3px 0', textAlign: 'right' }}
                  type="number" min="1" value={l.qty} onChange={e => updLine(i, 'qty', e.target.value)} />
                <input className="fi" style={{ border: 'none', background: 'transparent', padding: '3px 0', textAlign: 'right' }}
                  type="number" min="0" step="0.01" value={l.unit} onChange={e => updLine(i, 'unit', e.target.value)} />
                <div style={{ fontSize: 12, textAlign: 'right', fontWeight: 500 }}>€{fmt(l.total)}</div>
                <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ac)', fontSize: 14, padding: 2 }}
                  onClick={() => removeLine(i)} disabled={lines.length === 1}>×</button>
              </div>
            ))}
          </div>
        </div>

        {/* ── Totals + settings ── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 12 }}>
          <div>
            <div className="fg">
              <label className="fl">Payment Method</label>
              <select className="fi" value={f.method} onChange={e => upd('method', e.target.value)}>
                {METHODS.map(m => <option key={m} value={m}>{METHOD_LABELS[m]}</option>)}
              </select>
            </div>
            <div className="fg">
              <label className="fl">Status</label>
              <select className="fi" value={f.status} onChange={e => upd('status', e.target.value)}>
                <option value="draft">📝 Draft (no number assigned)</option>
                <option value="issued">📄 Issued</option>
                <option value="paid">✅ Paid</option>
                <option value="overdue">⚠️ Overdue</option>
              </select>
            </div>
            <div className="fg">
              <label className="fl">Notes</label>
              <textarea className="fi" rows={2} style={{ resize: 'vertical' }} value={f.notes} onChange={e => upd('notes', e.target.value)} placeholder="Thank you for your business!" />
            </div>
          </div>

          <div style={{ background: 'var(--sur2)', borderRadius: 'var(--r)', border: '1px solid var(--bd)', padding: 12, fontSize: 12, alignSelf: 'start' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ color: 'var(--tx3)' }}>Subtotal</span>
              <span>€{fmt(subtotal)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4, gap: 4 }}>
              <span style={{ color: 'var(--tx3)' }}>Discount</span>
              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                <input type="number" min="0" step="0.01" className="fi" style={{ width: 60, padding: '2px 5px', fontSize: 11 }}
                  value={f.discount} onChange={e => upd('discount', e.target.value)} />
                <select className="fi" style={{ width: 52, padding: '2px 4px', fontSize: 11 }}
                  value={f.discType} onChange={e => upd('discType', e.target.value)}>
                  <option value="eur">€</option>
                  <option value="%">%</option>
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4, gap: 4 }}>
              <span style={{ color: 'var(--tx3)' }}>VAT (%)</span>
              <input type="number" min="0" max="100" className="fi" style={{ width: 60, padding: '2px 5px', fontSize: 11 }}
                value={f.vatRate} onChange={e => upd('vatRate', e.target.value)} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <span style={{ color: 'var(--tx3)', fontSize: 11 }}>VAT Mode</span>
              <div style={{ display: 'flex', gap: 0, border: '1px solid var(--bd)', borderRadius: 5, overflow: 'hidden' }}>
                <button type="button" onClick={() => setVatMode('inclusive')}
                  style={{ padding: '2px 8px', fontSize: 10, fontWeight: 600, border: 'none', cursor: 'pointer',
                    background: vatMode === 'inclusive' ? 'var(--ac)' : 'var(--sur)', color: vatMode === 'inclusive' ? '#fff' : 'var(--tx3)' }}>
                  Inclusive
                </button>
                <button type="button" onClick={() => setVatMode('exclusive')}
                  style={{ padding: '2px 8px', fontSize: 10, fontWeight: 600, border: 'none', borderLeft: '1px solid var(--bd)', cursor: 'pointer',
                    background: vatMode === 'exclusive' ? 'var(--ac)' : 'var(--sur)', color: vatMode === 'exclusive' ? '#fff' : 'var(--tx3)' }}>
                  Exclusive
                </button>
              </div>
            </div>
            <div style={{ fontSize: 9, color: 'var(--tx3)', marginBottom: 4 }}>
              {vatMode === 'inclusive' ? 'Prices include VAT' : 'VAT added on top of prices'}
            </div>
            <div style={{ borderTop: '1px solid var(--bd)', marginTop: 6, paddingTop: 6, display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 13 }}>
              <span>Total</span>
              <span style={{ color: 'var(--ac)' }}>€{fmt(total)}</span>
            </div>
          </div>
        </div>
      </ModalBody>

      <ModalFooter>
        <button className="btn btn-ghost" onClick={close}>Cancel</button>
        <button className="btn btn-primary" onClick={submit}>Create Pro Forma</button>
      </ModalFooter>
    </Modal>
  );
}
